import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrgId } from "@/shared/auth/orgContext";
import {
  deletePolicyDoc,
  getPolicyDocDownloadUrl,
  listPolicyDocs,
  uploadPolicyDoc,
  type PolicyDoc,
  type PolicyDocDownloadUrl,
} from "../api/iaComplianceApi";
import { mutationErrorMessage } from "@/shared/utils/mutationErrorMessage";
import { iaKeys } from "./iaComplianceKeys";

/**
 * Backend reasons (e.g. upload-complete's 400 "size mismatch") are buried by
 * axios under a generic "Request failed with status code 400" — surface them.
 * The agent API uses the same `{ error }` body convention as the incidents
 * API did, so this is an alias rather than a reimplementation. See
 * mutationErrorMessage for why shared/api/apiError is NOT a drop-in here.
 */
export const iaErrorMessage = mutationErrorMessage;

/** Trimmed single-file variant of useStagedUpload's StagedFile row. */
export interface PolicyDocUploadState {
  status: "idle" | "uploading" | "failed";
  fileName?: string;
  progress: number;
  error?: string;
}

const IDLE_UPLOAD: PolicyDocUploadState = { status: "idle", progress: 0 };

export interface UseIaPolicyDocsResult {
  docs: PolicyDoc[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Single-file upload orchestration (init → presigned PUT → complete). */
  upload: (file: File) => void;
  uploadState: PolicyDocUploadState;
  clearUploadError: () => void;
  /** Deletes a doc and refreshes the list. Rejects with the API reason. */
  remove: (docId: string) => Promise<void>;
  removingDocId: string | null;
  /**
   * ONE-SHOT presigned-URL mint for viewing a policy PDF. Each mint writes a
   * CJIS audit row server-side, so this must only run on an explicit user
   * action — never on a timer or render (useCustomFilePreview rationale).
   */
  mintDownloadUrl: (docId: string) => Promise<PolicyDocDownloadUrl>;
}

/**
 * Policy-document state for the Internal Affairs tab: the station's available
 * policy PDFs plus upload / delete / view-URL actions. Station-scoped via the
 * backend authorizer; queries gate on orgId like the rest of Station Admin.
 */
export function useIaPolicyDocs(): UseIaPolicyDocsResult {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  const docsQuery = useQuery({
    queryKey: iaKeys.docs(orgId ?? ""),
    queryFn: ({ signal }) => listPolicyDocs(signal),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const invalidateDocs = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: iaKeys.docs(orgId ?? "") }),
    [queryClient, orgId],
  );

  const [uploadState, setUploadState] =
    useState<PolicyDocUploadState>(IDLE_UPLOAD);

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      uploadPolicyDoc(file, (percent) =>
        setUploadState((s) =>
          s.status === "uploading" ? { ...s, progress: percent } : s,
        ),
      ),
    onMutate: (file) => {
      setUploadState({ status: "uploading", fileName: file.name, progress: 0 });
    },
    onSuccess: () => {
      setUploadState(IDLE_UPLOAD);
      void invalidateDocs();
    },
    onError: (err, file) => {
      setUploadState({
        status: "failed",
        fileName: file.name,
        progress: 0,
        error: iaErrorMessage(err, "Upload failed. Please try again."),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => deletePolicyDoc(docId),
    onSuccess: () => void invalidateDocs(),
  });

  const mintMutation = useMutation({
    mutationFn: (docId: string) => getPolicyDocDownloadUrl(docId),
  });

  return {
    docs: docsQuery.data ?? [],
    // Disabled-while-orgId-resolves must read as loading (React Query v5
    // keeps isPending true for disabled queries — same as useStationActivity).
    isLoading: docsQuery.isPending,
    isError: docsQuery.isError,
    refetch: () => void docsQuery.refetch(),
    upload: (file) => uploadMutation.mutate(file),
    uploadState,
    clearUploadError: () =>
      setUploadState((s) => (s.status === "failed" ? IDLE_UPLOAD : s)),
    remove: async (docId) => {
      await deleteMutation.mutateAsync(docId);
    },
    removingDocId: deleteMutation.isPending
      ? (deleteMutation.variables ?? null)
      : null,
    mintDownloadUrl: (docId) => mintMutation.mutateAsync(docId),
  };
}
